import Vue from 'vue'
import { mapState, mapGetters } from 'vuex'
import { SimplePoster } from '../models/poster'
import { PosterWithLocales, SimplePosterWithLocales } from '../store/poster'
import { PosterType } from '@/services/poster'

export interface PosterUserComponenData {
  posterType: PosterType
  ptypeUnwatch: { [key: string]: () => void }
  syncPromise: Promise<void> | null
  sposter: SimplePoster | null
  poster: PosterWithLocales | null
  reloaded: boolean
  filter: ((p: SimplePoster) => boolean) | null
  simple: boolean
  lazy: boolean
  dialog_: boolean
}

export const PosterUser = Vue.extend({
  props: {
    value: [Number, String],
  },

  data(): PosterUserComponenData {
    return {
      posterType: 'user',
      ptypeUnwatch: {},
      syncPromise: null,
      sposter: null,
      poster: null,
      reloaded: false,
      filter: null,
      simple: false,
      lazy: false,
      dialog_: false,
    }
  },

  computed: {
    ...mapState('poster', ['posters']),
    ...mapGetters('poster', {
      getPoster: 'poster',
    }),

    items(): SimplePosterWithLocales[] {
      return this.posters[this.posterType] || []
    },

    visible: {
      get(this: any) {
        return Boolean(this.sposter)
      },

      set(v: boolean) {
        if (!v) {
          this.$emit('input', null)
        }
      },
    },
  },

  methods: {
    async sync(): Promise<void> {
      while (this.syncPromise) {
        await this.syncPromise
      }
    },

    resync(next: any): Promise<void> {
      const p = this.syncPromise || Promise.resolve()
      const sp = p.then(next)
      this.syncPromise = sp
      this.syncPromise.then(
        () => this.syncPromise === sp && (this.syncPromise = null)
      )
      return this.sync()
    },

    loadPosters() {
      return this.resync(async () => {
        if (!this.posters[this.posterType]) {
          await this.$store.dispatch('poster/fetchPosters', this.posterType)
        }

        if (!this.lazy) {
          this.updateItem()
        }
      })
    },

    fetchPoster(
      poster: SimplePoster,
      reload = false
    ): Promise<PosterWithLocales> {
      return this.$store.dispatch('poster/getDetailedPoster', {
        type: this.posterType,
        poster,
        reload,
      })
    },

    updateItem(reload?: boolean) {
      return this.resync(async () => {
        if (this.reloaded || !this.posterType) {
          return
        }

        const { value } = this
        let poster: SimplePoster

        if (typeof value === 'string') {
          poster = this.getPoster({ type: this.posterType, id: value })
        } else if (typeof value === 'number' && value !== -1) {
          poster = this.items[value] || null
        } else {
          this.sposter = null
          this.poster = null
          return
        }

        this.sposter = poster
        if (!poster || this.simple) {
          return
        }

        const prevId = this.poster?.id_poster
        try {
          this.poster = await this.fetchPoster(poster, reload)
        } catch (err) {
          this.poster = null
          return
        }

        this.reloaded = Boolean(prevId && prevId === this.poster?.id_poster)
      })
    },

    clearPoster() {
      this.sposter = null
      this.poster = null
      this.reloaded = false
      this.setValue(null)
    },

    setValue(v: string | number | null) {
      this.$emit('input', v)
    },

    shift(delta: number, event?: string) {
      if (!this.sposter) {
        return
      }

      const { filter, items } = this
      let { index } = this.sposter

      if (!items || typeof index !== 'number') {
        if (event) {
          this.$emit(event)
        }

        return
      }

      if (filter) {
        let i
        for (i = 0; i < items.length; i++) {
          index = (index + delta) % items.length
          if (index < 0) {
            index += items.length
          }

          if (filter(items[index])) {
            break
          }
        }

        if (i === items.length) {
          index = -1
        }
      } else {
        index = (index + delta) % items.length
        if (index < 0) {
          index += items.length
        }
      }

      this.setValue(index)
    },

    previous() {
      this.shift(-1, 'previous')
    },

    next() {
      this.shift(1, 'next')
    },

    bindPosterType(prop: string) {
      this.ptypeUnwatch[prop] = this.$watch(
        prop,
        v => (this.posterType = v || 'user')
      )
      this.posterType = (this as any)[prop] || 'user'
    },

    unbindPosterType(prop: string) {
      this.ptypeUnwatch[prop]()
    },
  },

  watch: {
    async value(newValue: number, oldValue: number) {
      await this.sync()
      if (this.reloaded && newValue !== oldValue) {
        this.reloaded = false
      }

      if (!this.lazy) {
        this.updateItem()
      }
    },

    poster(v: PosterWithLocales) {
      if (!v || !this.dialog_) {
        return
      }

      const _targets = this.$refs.scrollTarget_ || this.$el
      if (!_targets) {
        return
      }

      const targets = Array.isArray(_targets) ? _targets : [_targets]
      targets.forEach((t: any) =>
        (t instanceof Vue ? t.$el : t).scrollIntoView()
      )
    },
  },

  async created() {
    this.loadPosters()
  },
})

export default PosterUser
